<script lang="ts">
  import TaskCard from "@/components/task/task-card.svelte"
  import type { TaskInstance } from "@/types"

  interface Props {
    tasks: TaskInstance[]
    title?: string
    emptyMessage?: string
    showActions?: boolean
    onComplete?: (taskInstance: TaskInstance) => void
    onApprove?: (taskInstance: TaskInstance) => void
    onCancel?: (taskInstanceId: number) => void
  }

  let {
    tasks,
    title,
    emptyMessage = "No tasks found",
    showActions = false,
    onComplete,
    onApprove,
    onCancel,
  }: Props = $props()

  const groupedTasks = $derived(groupTasksByStatus(tasks))

  function groupTasksByStatus(tasks: TaskInstance[]) {
    const groups: Record<string, TaskInstance[]> = {
      pending: [],
      completed: [],
      pending_approval: [],
      approved: [],
      paid: [],
    }

    for (const task of tasks) {
      let status: string = "pending"
      if (task.paid) {
        status = "paid"
      } else if (task.approved_at) {
        status = "approved"
      } else if (
        task.completed_at &&
        task.task_template?.requires_approval &&
        !task.approved_at
      ) {
        status = "pending_approval"
      } else if (task.completed_at && !task.task_template?.requires_approval) {
        status = "completed"
      }

      groups[status].push(task)
    }

    return groups
  }
</script>

<div class="space-y-6">
  {#if title}
    <h2 class="text-2xl font-semibold">{title}</h2>
  {/if}

  {#if tasks.length === 0}
    <div
      class="flex flex-col items-center justify-center rounded-lg border border-dashed p-8 text-center"
    >
      <p class="text-muted-foreground">{emptyMessage}</p>
    </div>
  {:else}
    <div class="space-y-6">
      {#if groupedTasks.pending.length > 0}
        <div class="space-y-4">
          <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {#each groupedTasks.pending as taskInstance (taskInstance.id)}
              <TaskCard
                {taskInstance}
                {showActions}
                onComplete={onComplete
                  ? () => onComplete(taskInstance)
                  : undefined}
                onApprove={onApprove
                  ? () => onApprove(taskInstance)
                  : undefined}
                onCancel={onCancel &&
                taskInstance.feedback &&
                taskInstance.feedback.trim() !== ""
                  ? () => onCancel(taskInstance.id)
                  : undefined}
              />
            {/each}
          </div>
        </div>
      {/if}

      {#if groupedTasks.pending_approval.length > 0}
        <div class="space-y-4">
          <h3 class="text-lg font-medium">Pending Approval</h3>
          <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {#each groupedTasks.pending_approval as taskInstance (taskInstance.id)}
              <TaskCard
                {taskInstance}
                {showActions}
                compactDate={true}
                onComplete={onComplete
                  ? () => onComplete(taskInstance)
                  : undefined}
                onApprove={onApprove
                  ? () => onApprove(taskInstance)
                  : undefined}
              />
            {/each}
          </div>
        </div>
      {/if}

      {#if groupedTasks.completed.length > 0}
        <div class="space-y-4">
          <h3 class="text-lg font-medium">Completed</h3>
          <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {#each groupedTasks.completed as taskInstance (taskInstance.id)}
              <TaskCard
                {taskInstance}
                {showActions}
                onComplete={onComplete
                  ? () => onComplete(taskInstance)
                  : undefined}
                onApprove={onApprove
                  ? () => onApprove(taskInstance)
                  : undefined}
              />
            {/each}
          </div>
        </div>
      {/if}

      {#if groupedTasks.approved.length > 0}
        <div class="space-y-4">
          <h3 class="text-lg font-medium">Approved</h3>
          <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {#each groupedTasks.approved as taskInstance (taskInstance.id)}
              <TaskCard
                {taskInstance}
                {showActions}
                onComplete={onComplete
                  ? () => onComplete(taskInstance)
                  : undefined}
                onApprove={onApprove
                  ? () => onApprove(taskInstance)
                  : undefined}
              />
            {/each}
          </div>
        </div>
      {/if}

      {#if groupedTasks.paid.length > 0}
        <div class="space-y-4">
          <h3 class="text-lg font-medium">Paid</h3>
          <div class="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {#each groupedTasks.paid as taskInstance (taskInstance.id)}
              <TaskCard
                {taskInstance}
                {showActions}
                onComplete={onComplete
                  ? () => onComplete(taskInstance)
                  : undefined}
                onApprove={onApprove
                  ? () => onApprove(taskInstance)
                  : undefined}
              />
            {/each}
          </div>
        </div>
      {/if}
    </div>
  {/if}
</div>
